import { types as t, Instance, getParent } from 'mobx-state-tree';
import CollectionArray from '~/mst/models/abstract/collection_array';
import Filterable from '~/mst/models/abstract/filterable';
import type { IReportModel } from './model';
import { formatDateFromIso } from '~/utils/format_date';
import { DateTime } from 'luxon';

const AssetFileModel = t
  .model('ReportAssetFileModel', {
    error: t.maybeNull(t.frozen()),
    status: t.maybeNull(t.string), // 'in_progress' | 'completed' | 'failed'
    url: t.maybeNull(t.string)
  })
  .views((self) => ({
    get isInProgress() {
      return self.status === 'in_progress';
    },
    get isFailed() {
      return self.status === 'failed';
    },
    get isReady() {
      return self.status === 'completed';
    },
    get errorText() {
      if (!self.error) return null;
      if (typeof self.error === 'string') return self.error;
      if (self.error.message) {
        return self.error.status ? `${self.error.message} (${self.error.status})` : self.error.message;
      }
      return JSON.stringify(self.error);
    }
  }));

const ReportAssetBaseModel = t
  .model('ReportAssetModel', {
    id: t.identifier,
    period_key: t.maybeNull(t.string),
    date_from: t.maybeNull(t.string),
    date_to: t.maybeNull(t.string),
    html: t.maybeNull(AssetFileModel),
    csv: t.maybeNull(AssetFileModel),
    pdf: t.maybeNull(AssetFileModel),
    summary: t.maybeNull(AssetFileModel)
  })
  .views((self) => ({
    get pdfUrl() {
      return self.pdf?.url || null;
    },
    get csvUrl() {
      return self.csv?.url || null;
    },
    get htmlUrl() {
      return self.html?.url || null;
    },
    get dateRangeLabel() {
      const report = getParent<IReportModel>(self, 3);
      const timeZone = report.configuration?.time_zone || 'UTC';
      const dateFrom = self.date_from;
      const dateTo = self.date_to;
      const fromDate = DateTime.fromISO(dateFrom, { zone: timeZone });
      const toDate = DateTime.fromISO(dateTo, { zone: timeZone });
      if (fromDate.hasSame(toDate, 'day')) {
        const dateStr = formatDateFromIso(dateFrom, { format: DateTime.DATE_MED_WITH_WEEKDAY, timezone: timeZone });
        return dateStr;
      }
      const fromDateStr = formatDateFromIso(dateFrom, { format: DateTime.DATE_MED_WITH_WEEKDAY, timezone: timeZone });
      const toDateStr = formatDateFromIso(dateTo, { format: DateTime.DATE_MED_WITH_WEEKDAY, timezone: timeZone });
      return `${fromDateStr} - ${toDateStr}`;
    },
    get valueLabelPair() {
      return {
        value: self.period_key,
        label: self.dateRangeLabel,
        model: self
      };
    }
  }));

const ReportAssetModel = ReportAssetBaseModel;

const ReportAssetsCollectionModel = t
  .compose(CollectionArray(ReportAssetModel), Filterable)
  .views((self) => {
    function getAsset(periodKey: string | null) {
      if (periodKey) {
        return self.findWhere({ period_key: periodKey });
      }
      return self.first;
    }
    return {
      getAsset(periodKey: string | null) {
        return getAsset(periodKey);
      },
      get valueLabelPairs() {
        return self.toArray
          .map((model: any) => model.valueLabelPair)
          .sort(({ model: modelA }, { model: modelB }) => {
            const dateA = modelA?.date_from ? DateTime.fromISO(modelA.date_from) : null;
            const dateB = modelB?.date_from ? DateTime.fromISO(modelB.date_from) : null;

            const timestampA = dateA?.isValid ? dateA.toMillis() : Number.NEGATIVE_INFINITY;
            const timestampB = dateB?.isValid ? dateB.toMillis() : Number.NEGATIVE_INFINITY;

            return timestampB - timestampA;
          });
      },
      get filteredList() {
        const report = getParent<IReportModel>(self);
        return self.filteredModels.map((asset: IReportAssetModel) => ({
          id: asset.id,
          report_name: report.name,
          report_id: report.id,
          date_from: asset.date_from,
          date_to: asset.date_to,
          date_range: asset.period_key,
          date_range_label: asset.dateRangeLabel,
          working_hours: report.configuration?.working_hours || null,
          html: asset.html,
          csv: asset.csv,
          pdf: asset.pdf
        }));
      }
    };
  })
  .named('ReportAssetsCollectionModel');

export interface IReportAssetModel extends Instance<typeof ReportAssetModel> {}
export interface IReportAssetsCollectionModel extends Instance<typeof ReportAssetsCollectionModel> {}

export { ReportAssetModel };
export default ReportAssetsCollectionModel;
